import { RefObject, useCallback, useEffect, useState } from "react";

export interface ScrollEdges {
  canScrollLeft: boolean;
  canScrollRight: boolean;
}

// Sub-pixel rounding tolerance so an element scrolled to its end is not
// reported as still scrollable.
const EPSILON = 1;

/**
 * Tracks whether a horizontally scrollable element has hidden content to the
 * left and/or right. Updates on scroll, on resize, and whenever `deps` change
 * (e.g. the list of items, which can alter the scrollable width).
 */
export const useScrollEdges = (
  ref: RefObject<HTMLElement>,
  deps: unknown[] = []
): ScrollEdges => {
  const [edges, setEdges] = useState<ScrollEdges>({
    canScrollLeft: false,
    canScrollRight: false
  });

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;
    setEdges({
      canScrollLeft: scrollLeft > EPSILON,
      canScrollRight: scrollLeft + clientWidth < scrollWidth - EPSILON
    });
  }, [ref]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    update();
    el.addEventListener("scroll", update, { passive: true });

    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(el);

    return () => {
      el.removeEventListener("scroll", update);
      resizeObserver.disconnect();
    };
    // `deps` lets callers re-measure when their content changes.
  }, [update, ...deps]);

  return edges;
};
